import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, 
  ArrowRight, 
  ArrowLeft,
  Mail,
  Phone,
  MessageSquare,
  CheckCircle2
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../../../components/ui/avatar';
import { api } from '../../../context/AuthContext';
import { toast } from 'sonner';

export default function TeamStep({ data, onNext, onBack }) {
  const [loading, setLoading] = useState(false);
  const [teamData, setTeamData] = useState(data);

  const handleContinue = async () => {
    setLoading(true);
    try {
      const response = await api.post('/onboarding/complete-team');
      setTeamData(response.data.team);
      toast.success('Team assigned!');
      onNext();
    } catch (error) {
      toast.error('Failed to assign team');
    } finally {
      setLoading(false);
    }
  };

  const teamMembers = [
    {
      role: 'Project Manager',
      name: teamData?.project_manager?.name || 'To be assigned',
      email: teamData?.project_manager?.email || 'pm@godigitalstep.com',
      avatar: null,
      responsibilities: [
        'Your main point of contact',
        'Oversees all project activities',
        'Manages approvals and communication',
        'Ensures timely delivery of results'
      ]
    },
    {
      role: 'SEO Specialist',
      name: 'SEO Team',
      email: 'seo@godigitalstep.com',
      avatar: null,
      responsibilities: [
        'Technical SEO implementation',
        'Keyword research and strategy',
        'On-page optimization',
        'Performance monitoring'
      ]
    },
    {
      role: 'Content Strategist',
      name: 'Content Team',
      email: 'content@godigitalstep.com',
      avatar: null,
      responsibilities: [
        'Content planning and briefs',
        'Blog post creation',
        'Landing page optimization',
        'Content performance tracking'
      ]
    }
  ];

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="w-16 h-16 rounded-2xl bg-indigo-100 flex items-center justify-center mx-auto mb-4">
          <Users className="w-8 h-8 text-indigo-600" />
        </div>
        <h1 className="text-2xl font-bold font-heading mb-2">Meet Your Team</h1>
        <p className="text-slate-600 max-w-lg mx-auto">
          You'll be working with a dedicated team of digital marketing experts. Here's who will be supporting your success.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {teamMembers.map((member, index) => (
          <motion.div
            key={member.role}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <Card className="gds-card h-full">
              <CardContent className="p-6">
                <div className="text-center mb-4">
                  <Avatar className="w-16 h-16 mx-auto mb-3">
                    <AvatarImage src={member.avatar} />
                    <AvatarFallback className="bg-indigo-100 text-indigo-600 text-lg">
                      {member.name.split(' ').map(n => n[0]).join('')}
                    </AvatarFallback>
                  </Avatar>
                  <h3 className="font-semibold text-lg">{member.name}</h3>
                  <p className="text-sm text-indigo-600 font-medium">{member.role}</p>
                </div>
                
                <div className="space-y-3 mt-4">
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Mail className="w-4 h-4" />
                    <span>{member.email}</span>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-slate-100">
                  <p className="text-xs text-slate-500 font-medium mb-2">Responsibilities:</p>
                  <ul className="space-y-1">
                    {member.responsibilities.map((resp, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs text-slate-600">
                        <CheckCircle2 className="w-3 h-3 text-emerald-500 mt-0.5 flex-shrink-0" />
                        <span>{resp}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Communication Info */}
      <Card className="gds-card bg-gradient-to-r from-indigo-50 to-violet-50 border-indigo-200">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center shadow-sm">
              <MessageSquare className="w-6 h-6 text-indigo-600" />
            </div>
            <div>
              <h3 className="font-semibold mb-1">How We Communicate</h3>
              <p className="text-sm text-slate-600">
                All approvals and updates happen right here in your GDS dashboard. You'll receive notifications for anything requiring your attention. Your Project Manager is also available via email for any questions.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex justify-between pt-4">
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <Button 
          onClick={handleContinue}
          disabled={loading}
          className="gds-btn-primary"
        >
          {loading ? 'Setting up...' : 'Complete Setup'}
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}
